import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import dayjs, { Dayjs } from "dayjs";
import {
    getClassificationLabel,
    Imagery,
    ModelClassification,
    ImageryType,
    isWaterImageryType,
} from "@/types/imagery";
import classNames from "classnames";
import {
    Button,
    DatePicker,
    Divider,
    Select,
    SelectItem,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableColumn,
    TableHeader,
    TableRow,
} from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCheck,
    faThumbsDown,
    faStar,
    faClose,
    faCaretRight,
    faCaretLeft,
    faImage,
    faMap,
    faSlash,
} from "@fortawesome/free-solid-svg-icons";
import utc from "dayjs/plugin/utc";
import NStatusChip from "../common/NStatusChip";
import useRequest from "@/lib/request/useRequest";
import useAuth from "@/lib/auth/useAuth";
import { getTodayWithTimeTravel } from "@/lib/utils/date";
import { useTranslation } from "react-i18next";
import { Resource } from "@/types/user";
import {
    DateValue,
    getLocalTimeZone,
    parseDate,
} from "@internationalized/date";
import { MoistureStatus } from "@/types/water";

dayjs.extend(utc);

const CARD_SIZE = 44; // px — width and height of each square card
const CARD_GAP = 8; // px — corresponds to gap-2 in Tailwind

function DayImagesTable({
    images,
    selectedImage,
    setSelectedImage,
    onClose,
    onScore,
    onPrefer,
    dayLabel,
}: {
    images: Imagery[];
    selectedImage?: Imagery;
    setSelectedImage?: (image?: Imagery) => void;
    onClose: () => void;
    onScore?: (imagery: Imagery, classification: ModelClassification) => void;
    onPrefer?: (imagery: Imagery, manually_preferred: boolean) => void;
    dayLabel: string;
}) {
    const { isAtLeastDetail, isAtLeastSentinel } = useAuth();

    const showActions = isAtLeastSentinel(Resource.IMAGERY_TIMELINE);
    const showPreferredOnly =
        isAtLeastDetail(Resource.IMAGERY_TIMELINE) && !showActions;

    const columns = useMemo(() => {
        const base = [
            { key: "capture_date", label: "Capture date" },
            { key: "time", label: "Time" },
            { key: "n_status", label: "N Status" },
            { key: "source", label: "Source" },
            { key: "quality", label: "Quality" },
        ];
        if (showActions) base.push({ key: "actions", label: "Actions" });
        else if (showPreferredOnly)
            base.push({ key: "preferred", label: "Preferred" });
        return base;
    }, [showActions, showPreferredOnly]);

    const sortedImages = useMemo(() => {
        return [...(images ?? [])].sort((a, b) =>
            dayjs(b.capture_date).diff(dayjs(a.capture_date)),
        );
    }, [images]);

    const renderCell = useCallback(
        (item: Imagery, columnKey: string) => {
            switch (columnKey) {
                case "capture_date":
                    return dayjs
                        .utc(item.capture_date)
                        .local()
                        .format("YYYY-MM-DD");
                case "time":
                    return dayjs
                        .utc(item.capture_date)
                        .local()
                        .format("h:mm a");
                case "n_status":
                    return item.n_status ? (
                        <NStatusChip nStatus={item.n_status} />
                    ) : (
                        "--"
                    );
                case "source":
                    return item.imagery_subscription?.provider_name ?? "--";
                case "quality":
                    return (
                        <span className="flex flex-col gap-0.5">
                            {item.usable ? (
                                <span className="flex items-center gap-1 text-green-500">
                                    Good <FontAwesomeIcon icon={faCheck} />
                                </span>
                            ) : (
                                <span className="flex items-center gap-1 text-red-500">
                                    Bad <FontAwesomeIcon icon={faThumbsDown} />
                                </span>
                            )}
                            {showActions && (
                                <span>
                                    Model:{" "}
                                    {getClassificationLabel(
                                        item.model_classification,
                                    )}
                                </span>
                            )}
                        </span>
                    );
                case "actions":
                    return (
                        <div className="flex gap-1">
                            <Button
                                size="sm"
                                isIconOnly
                                color="success"
                                aria-label="Good"
                                onPress={() =>
                                    onScore?.(item, ModelClassification.good)
                                }
                            >
                                <FontAwesomeIcon icon={faCheck} />
                            </Button>
                            <Button
                                size="sm"
                                isIconOnly
                                color="danger"
                                aria-label="Bad"
                                onPress={() =>
                                    onScore?.(item, ModelClassification.bad)
                                }
                            >
                                <FontAwesomeIcon icon={faThumbsDown} />
                            </Button>
                            <Button
                                size="sm"
                                isIconOnly
                                isDisabled={!item.usable}
                                variant="light"
                                aria-label="Preferred"
                                onPress={() =>
                                    onPrefer?.(item, !item.active_for_day)
                                }
                            >
                                <FontAwesomeIcon
                                    icon={faStar}
                                    color={
                                        item.active_for_day
                                            ? "#FFD700"
                                            : undefined
                                    }
                                />
                            </Button>
                        </div>
                    );
                case "preferred":
                    return (
                        <div className="flex gap-1">
                            <Button
                                size="sm"
                                isIconOnly
                                isDisabled={!item.usable}
                                variant="light"
                                aria-label="Preferred"
                                onPress={() =>
                                    onPrefer?.(item, !item.active_for_day)
                                }
                            >
                                <FontAwesomeIcon
                                    icon={faStar}
                                    color={
                                        item.active_for_day
                                            ? "#FFD700"
                                            : undefined
                                    }
                                />
                            </Button>
                        </div>
                    );
                default:
                    return null;
            }
        },
        [showActions, onScore, onPrefer],
    );

    return (
        <div className="flex flex-col h-[12rem] overflow-scroll w-full ">
            <div className="flex flex-row justify-between items-center gap-2 px-2 shrink-0">
                <span className="font-semibold text-sm">{dayLabel}</span>
                <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    onPress={onClose}
                    aria-label="Close"
                >
                    <FontAwesomeIcon icon={faClose} />
                </Button>
            </div>
            <Divider />
            <div className="overflow-auto pb-24 w-full flex-1 p-1">
                <Table
                    radius="none"
                    aria-label="Images for day"
                    selectionMode="single"
                    removeWrapper
                    selectedKeys={
                        selectedImage
                            ? new Set([String(selectedImage.id)])
                            : new Set()
                    }
                    onSelectionChange={(keys) => {
                        const id = [...keys][0];
                        if (id == null) {
                            setSelectedImage?.(undefined);
                        } else {
                            const found = sortedImages.find(
                                (im) => String(im.id) === String(id),
                            );
                            if (found) setSelectedImage?.(found);
                        }
                    }}
                    color="primary"
                    classNames={{
                        td: "px-2 rounded-none",
                        tr: "cursor-pointer rounded-none first:rounded-none last:rounded-none",
                        th: "rounded-none first:rounded-none last:rounded-none",
                    }}
                >
                    <TableHeader columns={columns} className="bg-pink-500">
                        {(col) => (
                            <TableColumn
                                className="text-xs p-0 rounded-none "
                                key={col.key}
                            >
                                {col.label}
                            </TableColumn>
                        )}
                    </TableHeader>
                    <TableBody
                        items={sortedImages}
                        emptyContent="No images"
                        loadingContent={<Spinner label="Loading..." />}
                    >
                        {(item) => (
                            <TableRow key={item.id} className="rounded-none">
                                {(columnKey) => (
                                    <TableCell className="text-xs py-1">
                                        {renderCell(item, columnKey as string)}
                                    </TableCell>
                                )}
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            <div>
                <Divider />
            </div>
        </div>
    );
}

export default function ImageryTimelineV2() {
    const { t } = useTranslation("messages");
    const {
        filteredImagery,
        fieldImageryQuery,
        selectedCropCycle,
        setSelectedImage,
        selectedImage,
        imageType,
        setImageType,
        imageryOptions,
        fieldWaterQuery,
        selectedDate,
        setSelectedDate,
        imageryVisible,
        setImageryVisible,
    } = useFieldDashboard() ?? {};
    const { useMutateRequest } = useRequest();
    const [containerWidth, setContainerWidth] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const observer = new ResizeObserver(([entry]) => {
            setContainerWidth(entry.contentRect.width);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const numberOfDays =
        containerWidth > 0
            ? Math.max(
                  1,
                  Math.floor(
                      (containerWidth + CARD_GAP) / (CARD_SIZE + CARD_GAP),
                  ),
              )
            : 0;

    const [timelineStartDate, setTimelineStartDate] = useState<Dayjs>(() =>
        dayjs().subtract(4, "day"),
    );
    const timelineStartDateRef = useRef<Dayjs>(timelineStartDate);
    const [expandedDay, setExpandedDay] = useState<string | null>(null);

    useEffect(() => {
        timelineStartDateRef.current = timelineStartDate;
    }, [timelineStartDate]);

    const imagesInCycle = useMemo(() => {
        const data = fieldImageryQuery?.data;
        if (!data) return [];
        const cycleEnd = selectedCropCycle?.end_date
            ? dayjs(selectedCropCycle.end_date).isBefore(
                  getTodayWithTimeTravel(),
              )
                ? dayjs(selectedCropCycle.end_date)
                : getTodayWithTimeTravel()
            : getTodayWithTimeTravel();
        return data.filter((image) => {
            const captureDate = dayjs(image.capture_date);
            if (!selectedCropCycle) return true;
            return (
                captureDate.isAfter(
                    dayjs(selectedCropCycle.start_date).subtract(1, "day"),
                ) && captureDate.isBefore(cycleEnd.add(1, "day"))
            );
        });
    }, [fieldImageryQuery?.data, selectedCropCycle]);

    const waterEstimates = useMemo(() => {
        if (!fieldWaterQuery?.data) return [];
        const cycleEnd = selectedCropCycle?.end_date
            ? dayjs(selectedCropCycle.end_date).isBefore(
                  getTodayWithTimeTravel(),
              )
                ? dayjs(selectedCropCycle.end_date)
                : getTodayWithTimeTravel()
            : getTodayWithTimeTravel();
        return fieldWaterQuery.data.filter((water) => {
            const date = dayjs(water.date);
            return (
                date.isAfter(selectedCropCycle?.start_date) &&
                date.isBefore(cycleEnd)
            );
        });
    }, [fieldWaterQuery?.data, selectedCropCycle]);

    const centerOffset = Math.floor(numberOfDays / 2);

    useEffect(() => {
        if (!imagesInCycle.length) {
            setSelectedImage?.(undefined);
            return;
        }
        const mostRecent = imagesInCycle
            .filter((im) => im.active_for_day)
            .reduce((prev, cur) =>
                new Date(cur.capture_date) > new Date(prev.capture_date)
                    ? cur
                    : prev,
            );
        if (mostRecent) {
            setSelectedImage?.(mostRecent);
            setTimelineStartDate(
                dayjs(mostRecent.capture_date).subtract(centerOffset, "day"),
            );
            setSelectedDate?.(
                dayjs(mostRecent.capture_date).format("YYYY-MM-DD"),
            );
        }
    }, [imagesInCycle, setSelectedImage, centerOffset, setSelectedDate]);

    const timelineDays = useMemo(() => {
        if (numberOfDays === 0) return [];
        return Array(numberOfDays)
            .fill(0)
            .map((_, i) => timelineStartDate.add(i, "day"));
    }, [timelineStartDate, numberOfDays]);

    useEffect(() => {
        if (!selectedDate || numberOfDays === 0) return;

        const selected = dayjs(selectedDate).startOf("day");
        const start = timelineStartDateRef.current.startOf("day");
        const end = start.add(numberOfDays - 1, "day");

        if (selected.isBefore(start) || selected.isAfter(end)) {
            setTimelineStartDate(selected.subtract(centerOffset, "day"));
        }
    }, [selectedDate, centerOffset, numberOfDays]);

    const allImagesByDate = useMemo(() => {
        return (
            fieldImageryQuery?.data?.reduce<Record<string, Imagery[]>>(
                (acc, im) => {
                    const key = dayjs
                        .utc(im.capture_date)
                        .local()
                        .format("YYYY-MM-DD");
                    if (!acc[key]) acc[key] = [];
                    acc[key].push(im);
                    return acc;
                },
                {},
            ) ?? {}
        );
    }, [fieldImageryQuery?.data]);

    const imagesByDate = useMemo(() => {
        return (
            filteredImagery?.reduce<Record<string, Imagery>>((acc, im) => {
                acc[dayjs.utc(im.capture_date).local().format("YYYY-MM-DD")] =
                    im;
                return acc;
            }, {}) ?? {}
        );
    }, [filteredImagery]);

    const updateImagery = useMutateRequest({
        successMessage: t("success.imagery_updated"),
        errorMessage: t("error.failed_to_update_imagery"),
    });

    const onScore = useCallback(
        (imagery: Imagery, classification: ModelClassification) => {
            updateImagery.mutate(
                {
                    url: `imagery/${imagery.id}/score/`,
                    data: { user_classification: classification },
                    method: "put",
                },
                { onSuccess: () => fieldImageryQuery?.refetch() },
            );
        },
        [fieldImageryQuery, updateImagery],
    );

    const onPrefer = useCallback(
        (imagery: Imagery, manually_preferred: boolean) => {
            updateImagery.mutate(
                {
                    url: `imagery/${imagery.id}/manually_preferred/`,
                    data: { manually_preferred },
                    method: "put",
                },
                { onSuccess: () => fieldImageryQuery?.refetch() },
            );
        },
        [fieldImageryQuery, updateImagery],
    );

    const expandedDayImages = expandedDay
        ? (allImagesByDate[expandedDay] ?? [])
        : [];
    const expandedDayLabel = expandedDay
        ? dayjs(expandedDay).format("ddd, MMM D, YYYY")
        : "";

    const selectedDateKey = selectedDate
        ? dayjs(selectedDate).format("YYYY-MM-DD")
        : null;
    const selectedDayImageCount = selectedDateKey
        ? (allImagesByDate[selectedDateKey]?.length ?? 0)
        : 0;

    return (
        <div className="flex flex-col gap-2 w-full relative">
            {expandedDay && (
                <DayImagesTable
                    images={expandedDayImages}
                    dayLabel={expandedDayLabel}
                    selectedImage={selectedImage}
                    setSelectedImage={setSelectedImage}
                    onClose={() => setExpandedDay(null)}
                    onScore={onScore}
                    onPrefer={onPrefer}
                />
            )}
            <div className="flex flex-row items-center gap-2 flex-wrap justify-between px-2 pt-2">
                <div className="flex flex-row items-center gap-2">
                    {selectedDayImageCount >= 1 && selectedDateKey && (
                        <div>
                            <Button
                                size="sm"
                                color="primary"
                                variant={expandedDay ? "solid" : "flat"}
                                aria-label="View image details"
                                onPress={() =>
                                    setExpandedDay(
                                        expandedDay ? null : selectedDateKey,
                                    )
                                }
                                endContent={<FontAwesomeIcon icon={faImage} />}
                            >
                                View Images
                            </Button>
                        </div>
                    )}

                    <DatePicker
                        aria-label="Select Date"
                        showMonthAndYearPickers
                        size="sm"
                        className="w-[9rem]"
                        value={parseDate(
                            dayjs(selectedDate).format("YYYY-MM-DD"),
                        )}
                        onChange={(e) => {
                            const d = dayjs(e?.toDate(getLocalTimeZone()));
                            setTimelineStartDate(
                                d.subtract(centerOffset, "day"),
                            );
                            setSelectedDate?.(d.format("YYYY-MM-DD"));
                            const imOnDate =
                                imagesByDate[d.format("YYYY-MM-DD")];
                            if (imOnDate) setSelectedImage?.(imOnDate);
                        }}
                        isDateUnavailable={(date: DateValue) =>
                            isWaterImageryType(imageType)
                                ? false
                                : !waterEstimates.find(
                                      (water) =>
                                          water.date ===
                                          dayjs(
                                              date.toDate(getLocalTimeZone()),
                                          ).format("YYYY-MM-DD"),
                                  ) &&
                                  !imagesByDate[
                                      dayjs(
                                          date.toDate(getLocalTimeZone()),
                                      ).format("YYYY-MM-DD")
                                  ]
                        }
                    />
                </div>
                <div className="flex flex-row items-center gap-2">
                    <Button
                        isIconOnly
                        size="sm"
                        variant="flat"
                        onPress={() => setImageryVisible?.((v) => !v)}
                        aria-label="Toggle imagery visibility"
                    >
                        <span className="relative inline-flex items-center justify-center">
                            <FontAwesomeIcon
                                icon={faMap}
                                className={
                                    imageryVisible ? "text-primary" : "text-default-400"
                                }
                            />
                            {!imageryVisible && (
                                <FontAwesomeIcon
                                    icon={faSlash}
                                    className="absolute text-default-400 scale-110"
                                />
                            )}
                        </span>
                    </Button>
                    <Select
                        size="sm"
                        className="w-[8rem]"
                        selectedKeys={
                            imageryOptions &&
                            imageryOptions.length > 0 &&
                            imageType
                                ? [imageType]
                                : []
                        }
                        onChange={(e) =>
                            setImageType?.(e.target.value as ImageryType)
                        }
                        classNames={{
                            base: "min-w-[12rem]",
                        }}
                        aria-label="Select Imagery Type"
                    >
                        {(imageryOptions ?? []).map((opt) => (
                            <SelectItem key={opt.key}>{opt.label}</SelectItem>
                        ))}
                    </Select>
                </div>
            </div>
            <div className="overflow-x-auto w-full min-w-0 px-2 pb-2">
                <div className="flex flex-row items-center gap-3 w-full">
                    <div className="flex flex-row items-center gap-1 shrink-0">
                        <Button
                            isIconOnly
                            size="sm"
                            variant="flat"
                            color="primary"
                            aria-label="Previous 7 days"
                            onPress={() =>
                                setTimelineStartDate((d) =>
                                    d.subtract(numberOfDays, "day"),
                                )
                            }
                        >
                            <FontAwesomeIcon
                                icon={faCaretLeft}
                                className="text-xs"
                            />
                        </Button>
                    </div>
                    <div
                        ref={containerRef}
                        className="flex flex-1 flex-row gap-2 py-0.5 sm:p-1 min-w-0 overflow-hidden justify-center"
                    >
                        {timelineDays.map((day) => {
                            const dateKey = day.format("YYYY-MM-DD");
                            const imageOnDate = imagesByDate[dateKey];
                            const usable = imageOnDate?.usable;
                            const nStatus = imageOnDate?.n_status;
                            const waterEstimate = isWaterImageryType(imageType)
                                ? waterEstimates.find(
                                      (water) => water.date === dateKey,
                                  )
                                : undefined;
                            const moistureStatus = isWaterImageryType(imageType)
                                ? waterEstimate?.moisture_status
                                : undefined;
                            const selected = selectedDate === dateKey;

                            return (
                                <div
                                    key={dateKey}
                                    className="flex flex-col items-center shrink-0"
                                >
                                    <button
                                        type="button"
                                        style={{
                                            width: CARD_SIZE,
                                            height: CARD_SIZE,
                                        }}
                                        onClick={() => {
                                            if (imageOnDate)
                                                setSelectedImage?.(imageOnDate);
                                            if (expandedDay)
                                                setExpandedDay(dateKey);
                                            setSelectedDate?.(dateKey);
                                        }}
                                        className={classNames(
                                            "rounded border-2 flex flex-col items-center justify-center transition-transform ",
                                            "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-primary",
                                            {
                                                "ring-2 ring-primary ring-offset-1":
                                                    selected,
                                                "border-red-500 text-red-600 bg-red-100":
                                                    (usable &&
                                                        nStatus === "red" &&
                                                        !waterEstimate) ||
                                                    moistureStatus ===
                                                        MoistureStatus.APPROCHING_DEFICIENCY,
                                                "border-green-500 text-green-600 bg-green-100":
                                                    (usable &&
                                                        nStatus === "green" &&
                                                        !waterEstimate) ||
                                                    moistureStatus ===
                                                        MoistureStatus.OPTIMAL,
                                                "border-yellow-500 text-yellow-600 bg-yellow-100":
                                                    usable &&
                                                    nStatus === "yellow" &&
                                                    !waterEstimate,
                                                "border-default-500 text-default-800 bg-default-200":
                                                    imageOnDate &&
                                                    (!usable || !nStatus) &&
                                                    !moistureStatus,
                                                "border-blue-400 text-blue-600 bg-blue-100":
                                                    moistureStatus ===
                                                    MoistureStatus.APPROACHING_SATURATION,
                                                "border-default-200 text-default-400 bg-default-50 cursor-not-allowed ":
                                                    !imageOnDate &&
                                                    !waterEstimate,
                                                "cursor-pointer hover:scale-105":
                                                    imageOnDate ||
                                                    waterEstimate,
                                            },
                                        )}
                                        disabled={
                                            !imageOnDate && !waterEstimate
                                        }
                                    >
                                        <span className="text-sm font-medium leading-tight">
                                            {day.format("MMM")}
                                        </span>
                                        <span className="text-xs font-semibold leading-tight">
                                            {day.format("DD")}
                                        </span>
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                    <div className="flex flex-row items-center gap-1 shrink-0">
                        <Button
                            isIconOnly
                            size="sm"
                            variant="flat"
                            color="primary"
                            aria-label="Next 7 days"
                            onPress={() =>
                                setTimelineStartDate((d) =>
                                    d.add(numberOfDays, "day"),
                                )
                            }
                        >
                            <FontAwesomeIcon
                                icon={faCaretRight}
                                className="text-xs"
                            />
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
